import type {
  BridgeChartIndicatorSeries,
  BridgeChartSeriesPoint,
  BridgeChartStudyCode,
  BridgeChartStudyKind,
  NormalizedIndicatorSeries
} from "../types/data";

export interface BridgeChartStudyDefinition {
  code: BridgeChartStudyCode;
  label: string;
  kind: BridgeChartStudyKind;
}

export const bridgeChartStudyCatalog: readonly BridgeChartStudyDefinition[] = Object.freeze([
  { code: "SMA_20", label: "SMA 20", kind: "Overlay" },
  { code: "SMA_50", label: "SMA 50", kind: "Overlay" },
  { code: "SMA_200", label: "SMA 200", kind: "Overlay" },
  { code: "EMA_20", label: "EMA 20", kind: "Overlay" },
  { code: "EMA_50", label: "EMA 50", kind: "Overlay" },
  { code: "RSI_14", label: "RSI 14", kind: "Pane" },
  { code: "MACD_12_26_9", label: "MACD", kind: "Pane" },
  { code: "ATR_14", label: "ATR 14", kind: "Pane" },
  { code: "VOLUME", label: "Volume", kind: "Pane" }
]);

const definitions = new Map(bridgeChartStudyCatalog.map(definition => [definition.code, definition]));

export function studyDefinition(code: string): BridgeChartStudyDefinition | undefined {
  return definitions.get(code as BridgeChartStudyCode);
}

export function normalizeStudySeries(input: BridgeChartIndicatorSeries[]): NormalizedIndicatorSeries[] {
  if (!Array.isArray(input)) throw new TypeError("BridgeChart studies must be an array.");
  const seen = new Set<BridgeChartStudyCode>();
  return input.map(raw => {
    const code = String(raw?.code || "").trim().toUpperCase() as BridgeChartStudyCode;
    const definition = studyDefinition(code);
    if (!definition) throw new TypeError(`BridgeChart study '${code || "(empty)"}' is unsupported.`);
    if (seen.has(code)) throw new TypeError(`BridgeChart study '${code}' is duplicated.`);
    seen.add(code);
    if (raw.kind !== definition.kind) {
      throw new TypeError(`BridgeChart study '${code}' must use the '${definition.kind}' kind.`);
    }

    const points = normalizePoints(raw.points, code);
    if (raw.available === true && points.length === 0) {
      throw new TypeError(`BridgeChart study '${code}' cannot claim availability without canonical points.`);
    }
    if (raw.available === false && points.length > 0) {
      throw new TypeError(`BridgeChart study '${code}' cannot contain points while unavailable.`);
    }
    const available = raw.available ?? points.length > 0;
    const unavailableReason = available
      ? null
      : normalizeUnavailableReason(raw.unavailableReason);
    const pointsByDate: Record<string, number> = {};
    points.forEach(point => { pointsByDate[point.date] = point.value; });
    return {
      code,
      label: String(raw.label || definition.label),
      kind: definition.kind,
      points,
      pointsByDate,
      available,
      unavailableReason
    };
  });
}

function normalizePoints(value: BridgeChartSeriesPoint[], code: BridgeChartStudyCode): BridgeChartSeriesPoint[] {
  if (!Array.isArray(value)) throw new TypeError(`BridgeChart study '${code}' points must be an array.`);
  const dates = new Set<string>();
  return value.map(point => {
    const date = String(point?.date || "");
    const number = Number(point?.value);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(number)) {
      throw new TypeError(`BridgeChart study '${code}' contains a malformed point.`);
    }
    if (dates.has(date)) throw new TypeError(`BridgeChart study '${code}' contains duplicate dates.`);
    dates.add(date);
    return { date, value: number };
  }).sort((left, right) => left.date.localeCompare(right.date));
}

function normalizeUnavailableReason(value: string | null | undefined): string {
  const reason = typeof value === "string" ? value.trim() : "";
  return reason || "CanonicalSeriesUnavailable";
}
